/**
 * Реферальная программа aisearch.ru: правила фронта без сети и React.
 *
 * Решения владельца (Arhitect/docs/proekt-referalka-bonusy.md):
 * новичку +100% к первому пополнению до 5 000 ₽ (от 200 ₽); партнёру 20%
 * от пополнений приглашённых на отдельный партнёрский баланс; вывод от
 * 10 000 ₽ на расчётный счёт ИП, организации или самозанятого; метка
 * перехода живёт 60 дней, и первая ссылка выигрывает.
 *
 * Сами деньги считает API (AI-Tech-API#234). Здесь только то, что нужно
 * фронту: метка перехода в куке, проверка формы вывода до отправки и
 * тексты. Модуль безопасен для middleware, сервера и браузера.
 *
 * Пока владелец не включил программу (`REFERRAL_ENABLED`), ручки API
 * отвечают 404, и всё видимое здесь не показывается вовсе: сайт обязан
 * выглядеть так же, как без рефералки.
 */

import { tolkoRusskij } from '@/lib/ais-ru-domen';

// ---------- метка перехода ----------

/** Кука с меткой перехода по ссылке. Только код и время, ничего личного. */
export const KUKA_REF = 'ais_ref';

/** Сколько живёт метка: 60 дней. API сверяет срок по `capturedAt`. */
export const SROK_REF_S = 60 * 24 * 60 * 60;

/**
 * Код или ник партнёра: латиница в нижнем регистре, цифры, дефис и
 * подчёркивание, хотя бы одна буква. Те же границы, что у `ParseRef` в
 * API: с ником (AI-Tech-API#262) в метку попадает и `ivan-petrov`, и
 * трёхбуквенный `ivo`, а прежнее правило «6–12 букв и цифр» такие
 * значения отбрасывало бы молча.
 */
const KOD = /^[a-z0-9_-]{1,32}$/;
/** Старые ссылки aisearch.ru: `?ref=<id клиента прежней версии>`. */
const LEGASI = /^[1-9]\d{0,8}$/;

export function jeRefKod(v: unknown): v is string {
  if (typeof v !== 'string') return false;
  if (LEGASI.test(v)) return true;
  return KOD.test(v) && /[a-z]/.test(v);
}

/** Значение `?ref=` из адреса: годное — код, остальное — `null`. */
export function refIzParametra(syroe: string | null | undefined): string | null {
  if (typeof syroe !== 'string') return null;
  const kod = syroe.trim().toLowerCase();
  return jeRefKod(kod) ? kod : null;
}

/** Значение куки: `код.секунды`. Точки в коде быть не может. */
export function znachenieRefKuki(kod: string, t: number): string {
  return `${kod}.${String(t)}`;
}

export interface MetkaRef {
  kod: string;
  /** Когда человек пришёл по ссылке, секунды эпохи. */
  t: number;
}

export function refIzKuki(znachenie: string | null | undefined): MetkaRef | null {
  if (!znachenie) return null;
  const chasti = znachenie.split('.');
  if (chasti.length !== 2) return null;
  const [kod, t] = chasti;
  if (!jeRefKod(kod) || !t || !/^\d{1,12}$/.test(t)) return null;
  return { kod, t: Number(t) };
}

/** Метка в формате API: RFC 3339. */
export function vremyaMetki(m: MetkaRef): string {
  return new Date(m.t * 1000).toISOString().replace(/\.\d{3}Z$/, 'Z');
}

export function parametryRefKuki(secure: boolean) {
  return {
    httpOnly: true,
    secure,
    sameSite: 'lax' as const,
    path: '/',
    maxAge: SROK_REF_S,
  };
}

/** Локальные стенды AiSearch: там рефералку можно потрогать руками. */
const LOKALNYE = ['aisearch.localhost', 'aisearch.local'];

/** Домен, на котором работает рефералка: aisearch.ru и локальный стенд. */
export function referalkaNaHoste(host: string | null | undefined): boolean {
  if (tolkoRusskij(host)) return true;
  const hostname = host?.split(':')[0]?.toLowerCase() ?? '';
  return LOKALNYE.includes(hostname);
}

/** То, что middleware знает о запросе. Своя форма ради тестов без Next. */
export interface ZaprosDlyaRef {
  nextUrl: { searchParams: URLSearchParams; protocol: string };
  headers: { get(name: string): string | null };
  cookies: { get(name: string): { value: string } | undefined };
}

export interface OtvetDlyaRef {
  cookies: { set(name: string, value: string, options: ReturnType<typeof parametryRefKuki>): void };
}

/** Предзагрузка браузера, а не переход: навёл мышь — ещё не пришёл. */
export function jePredzagruzka(headers: { get(name: string): string | null }): boolean {
  return (
    headers.get('purpose') === 'prefetch' ||
    headers.get('sec-purpose')?.includes('prefetch') === true
  );
}

/**
 * Поставить метку перехода с уже проверенным кодом.
 *
 * Общая для `?ref=` и короткой ссылки `/i/<код>`: правила одни и те же —
 * первая ссылка выигрывает (есть кука — не трогаем), предзагрузка не
 * считается, срок 60 дней. Возвращает, поставлена ли метка.
 */
export function postavitMetku(
  otvet: OtvetDlyaRef,
  zapros: Pick<ZaprosDlyaRef, 'headers' | 'cookies'> & { nextUrl: { protocol: string } },
  kod: string,
  now: number = Date.now(),
): boolean {
  if (!jeRefKod(kod)) return false;
  if (zapros.cookies.get(KUKA_REF)) return false;
  if (jePredzagruzka(zapros.headers)) return false;
  const secure =
    zapros.nextUrl.protocol === 'https:' || zapros.headers.get('x-forwarded-proto') === 'https';
  otvet.cookies.set(
    KUKA_REF,
    znachenieRefKuki(kod, Math.floor(now / 1000)),
    parametryRefKuki(secure),
  );
  return true;
}

/**
 * Запомнить переход по партнёрской ссылке (вызывается на выходе middleware).
 *
 * Первая ссылка выигрывает: есть кука — не трогаем. Предзагрузку браузера
 * пропускаем: навёл мышь — ещё не пришёл. Хост решает вызывающий.
 */
export function zapomnitRef<O extends OtvetDlyaRef>(
  otvet: O,
  zapros: ZaprosDlyaRef,
  host: string | null | undefined,
  now: number = Date.now(),
): O {
  if (!referalkaNaHoste(host)) return otvet;
  const kod = refIzParametra(zapros.nextUrl.searchParams.get('ref'));
  if (!kod) return otvet;
  postavitMetku(otvet, zapros, kod, now);
  return otvet;
}

// ---------- контракт API ----------

export type TipPoluchatelya = 'ip' | 'company' | 'self_employed';
export const TIPY_POLUCHATELYA: readonly TipPoluchatelya[] = ['ip', 'company', 'self_employed'];

export interface Rekvizity {
  recipientType: TipPoluchatelya;
  bik: string;
  corrAccount: string;
  account: string;
  inn: string;
  kpp?: string;
  recipientName: string;
}

export interface UsloviyaReferalki {
  rewardPercent: number;
  welcomePercent: number;
  welcomeMaxKopecks: number;
  welcomeMinTopupKopecks: number;
  minPayoutKopecks: number;
  newUserDays: number;
  linkDays: number;
}

export interface ObzorReferalki {
  code: string;
  /**
   * Ссылка партнёра. С AI-Tech-API#262 — короткая
   * `https://aisearch.ru/i/<ник|код>`, до него — `https://aisearch.ru/?ref=<код>`.
   */
  link: string;
  /**
   * Ник партнёра, `""` — ещё не выбран. Поля нет вовсе — API старше
   * #262, и выбор ника не предлагается: сохранить его было бы некуда.
   */
  nick?: string;
  /** Что вводить в поле «Промокод» при пополнении: ник, если выбран, иначе код. */
  promoCode?: string;
  /** Ссылка старого вида `?ref=`: работает и дальше. */
  refLink?: string;
  invited: number;
  paidCount: number;
  topupSumKopecks: number;
  earnedKopecks: number;
  balanceKopecks: number;
  availableKopecks: number;
  onHoldKopecks: number;
  terms: UsloviyaReferalki;
  lastPayoutDetails: Rekvizity | null;
}

export type StatusZayavki = 'created' | 'approved' | 'paid' | 'rejected' | 'error';

export interface ZayavkaNaVyvod {
  id: string;
  amountKopecks: number;
  status: StatusZayavki;
  adminNote?: string;
  requisites: Rekvizity;
  createdAt: string;
  updatedAt: string;
}

export type IshodPrivyazki = 'attached' | 'already' | 'rejected';

/** Ответ `PUT /account/referral/nick`. */
export interface OtvetNika {
  code: string;
  nick: string;
  promoCode: string;
  link: string;
  refLink: string;
}

// ---------- ник партнёра ----------

/**
 * Партнёр вводит основу ника, сервер дописывает к ней «-» и случайное
 * окончание из 4 знаков: ivan → ivan-k7qm (решение владельца 15.10.2026).
 * Красивые ники без окончания раздаём только мы сами, из кабинета их не
 * выбрать. Пределы ниже — для основы.
 */
export const NIK_MIN = 4;
export const NIK_MAX = 15;
/** Пример окончания для подсказки: настоящее выберет сервер. */
export const PRIMER_OKONCHANIYA = 'k7qm';

/**
 * Причины отказа в нике — те же имена, что `error.reason` у API.
 * `double_hyphen` браузер находит сам (у сервера это может прийти иной
 * причиной), `bad_word` и `reserved` знает только сервер.
 */
export type PrichinaNika =
  | 'length'
  | 'charset'
  | 'hyphen_edge'
  | 'double_hyphen'
  | 'no_letter'
  | 'reserved'
  | 'bad_word';

/** Ник в том виде, в каком его хранит API: без пробелов по краям, строчными. */
export function normalizovatNik(s: string): string {
  return s.trim().toLowerCase();
}

/**
 * Проверка ника до отправки — в том же порядке, что `ValidateNick` в
 * API: сначала алфавит, потом длина, дефисы, буква.
 *
 * Стоп-лист (`reserved`), недопустимые слова (`bad_word`) и занятость
 * проверяет только сервер: держать здесь копию его списков значило бы
 * однажды разойтись с ним, а ответ сервера всё равно приходит с точной
 * причиной.
 */
export function proveritNik(syroe: string): PrichinaNika | null {
  const n = normalizovatNik(syroe);
  if (!/^[a-z0-9-]*$/.test(n)) return 'charset';
  if (n.length < NIK_MIN || n.length > NIK_MAX) return 'length';
  if (n.startsWith('-') || n.endsWith('-')) return 'hyphen_edge';
  if (n.includes('--')) return 'double_hyphen';
  if (!/[a-z]/.test(n)) return 'no_letter';
  return null;
}

const PRICHINY_NIKA: readonly string[] = [
  'length',
  'charset',
  'hyphen_edge',
  'double_hyphen',
  'no_letter',
  'reserved',
  'bad_word',
];

/** Отказ сервера на выбор ника — в слова для человека. */
export function oshibkaNika(kod: string, prichina?: string): string {
  switch (kod) {
    case 'nick_invalid':
      return prichina && PRICHINY_NIKA.includes(prichina)
        ? TR.nik.prichiny[prichina as PrichinaNika]
        : TR.nik.negoden;
    case 'nick_taken':
      return TR.nik.zanyat;
    case 'nick_already_set':
      return TR.nik.uzheVybran;
    case 'rate_limited':
      return TR.nik.chasto;
    case 'session_required':
    case 'no_session':
    case 'unauthorized':
      return TR.oshibki.sessiya;
    case 'not_found':
      return TR.oshibki.nedostupno;
    default:
      return TR.nik.neVyshlo;
  }
}

// ---------- форма вывода ----------

/** Поля формы, по которым сервер и браузер говорят об ошибках. */
export type PoleFormy = keyof Rekvizity | 'amount' | 'consent';

export interface ZapolnenieFormy {
  recipientType: string;
  bik: string;
  corrAccount: string;
  account: string;
  inn: string;
  kpp: string;
  recipientName: string;
  /** Сумма в рублях, как человек её набрал: «10 000», «12500,50». */
  summa: string;
  consent: boolean;
}

/** Пробелы, табуляции, дефисы: люди ставят их в длинных номерах. */
function bezRazdelitelej(s: string): string {
  return s.replace(/[\s\-\u00a0]/g, '');
}

/** КПП нужен только организации. */
export function nuzhenKpp(tip: string): boolean {
  return tip === 'company';
}

/** Длина ИНН: 10 у организации, 12 у ИП и самозанятого. */
export function dlinaInn(tip: string): number {
  return tip === 'company' ? 10 : 12;
}

/** Рубли из строки в копейки. Не число или больше двух знаков — `null`. */
export function rubliVKopejki(s: string): number | null {
  const chistoe = bezRazdelitelej(s).replace(',', '.').replace(/₽$/, '');
  if (!/^\d{1,9}(\.\d{1,2})?$/.test(chistoe)) return null;
  const [celoe, drob = ''] = chistoe.split('.');
  return Number(celoe) * 100 + Number(drob.padEnd(2, '0'));
}

export interface ProverkaFormy {
  oshibki: Partial<Record<PoleFormy, string>>;
  /** Готовые к отправке реквизиты и сумма, если ошибок нет. */
  zapros?: Rekvizity & { amountKopecks: number };
}

/**
 * Проверка формы до отправки: только форматы.
 *
 * Контрольные суммы БИК, счетов и ИНН проверяет сервер и сам называет
 * поле с ошибкой: дублировать здесь алгоритм ЦБ значило бы держать две
 * копии правил о деньгах, которые однажды разойдутся.
 */
export function proveritFormu(
  f: ZapolnenieFormy,
  dostupno: number,
  minimum: number,
): ProverkaFormy {
  const oshibki: ProverkaFormy['oshibki'] = {};
  const tip = f.recipientType;
  if (!(TIPY_POLUCHATELYA as readonly string[]).includes(tip)) {
    oshibki.recipientType = TR.oshibki.recipientType;
  }
  const bik = bezRazdelitelej(f.bik);
  const corrAccount = bezRazdelitelej(f.corrAccount);
  const account = bezRazdelitelej(f.account);
  const inn = bezRazdelitelej(f.inn);
  const kpp = bezRazdelitelej(f.kpp).toUpperCase();
  const recipientName = f.recipientName.trim().split(/\s+/).join(' ');

  if (!/^\d{9}$/.test(bik)) oshibki.bik = TR.oshibki.bik;
  if (!/^\d{20}$/.test(corrAccount)) oshibki.corrAccount = TR.oshibki.corrAccount;
  if (!/^\d{20}$/.test(account)) oshibki.account = TR.oshibki.account;
  if (!new RegExp(`^\\d{${String(dlinaInn(tip))}}$`).test(inn)) {
    oshibki.inn = tip === 'company' ? TR.oshibki.inn10 : TR.oshibki.inn12;
  }
  if (nuzhenKpp(tip) && !/^\d{4}[\dA-Z]{2}\d{3}$/.test(kpp)) oshibki.kpp = TR.oshibki.kpp;
  const dlinaImeni = Array.from(recipientName).length;
  if (dlinaImeni < 3 || dlinaImeni > 300) oshibki.recipientName = TR.oshibki.recipientName;

  const kop = rubliVKopejki(f.summa);
  if (kop === null) oshibki.amount = TR.oshibki.summaNeChislo;
  else if (kop < minimum) oshibki.amount = TR.oshibki.summaMalo(minimum);
  else if (kop > dostupno) oshibki.amount = TR.oshibki.summaBolshe(dostupno);

  if (!f.consent) oshibki.consent = TR.oshibki.consent;

  if (Object.keys(oshibki).length > 0 || kop === null) return { oshibki };
  return {
    oshibki,
    zapros: {
      recipientType: tip as TipPoluchatelya,
      bik,
      corrAccount,
      account,
      inn,
      ...(nuzhenKpp(tip) ? { kpp } : {}),
      recipientName,
      amountKopecks: kop,
    },
  };
}

const POLYA_SERVERA: readonly PoleFormy[] = [
  'recipientType',
  'bik',
  'corrAccount',
  'account',
  'inn',
  'kpp',
  'recipientName',
];

/**
 * Отказ сервера на заявку — в ошибку у поля или общую строку.
 *
 * У `invalid_requisites` поле приходит в начале текста сервера
 * (`bik: checksum does not match the BIK`): по нему подсвечиваем строку
 * формы, а слова пишем свои — английский текст сервера человеку не
 * показываем.
 */
export function oshibkaServera(kod: string, detail?: string): { pole?: PoleFormy; tekst: string } {
  switch (kod) {
    case 'invalid_requisites': {
      const m = /^(\w+):\s*(.*)$/.exec(detail ?? '');
      const pole = POLYA_SERVERA.find((p) => p === m?.[1]);
      if (!pole) return { tekst: TR.oshibki.rekvizityObshchee };
      const kontrolnoe = /checksum/i.test(m?.[2] ?? '');
      if (kontrolnoe) return { pole, tekst: TR.oshibki.kontrolnoe(pole) };
      if (pole === 'kpp' && /only a company/i.test(m?.[2] ?? '')) {
        return { pole, tekst: TR.oshibki.kppLishnij };
      }
      return { pole, tekst: TR.oshibki.formatPolya(pole) };
    }
    case 'consent_required':
      return { pole: 'consent', tekst: TR.oshibki.consent };
    case 'amount_too_small':
      return { pole: 'amount', tekst: TR.oshibki.summaMaloObshchee };
    case 'insufficient_balance':
      return { pole: 'amount', tekst: TR.oshibki.nedostatochno };
    case 'rate_limited':
      return { tekst: TR.oshibki.chasto };
    case 'session_required':
    case 'no_session':
    case 'unauthorized':
      return { tekst: TR.oshibki.sessiya };
    case 'not_found':
      return { tekst: TR.oshibki.nedostupno };
    default:
      return { tekst: TR.oshibki.neVyshlo };
  }
}

/** «р/с …0005»: свой счёт узнать можно, а целиком на экране он не нужен. */
export function hvostScheta(account: string): string {
  return account.length > 4 ? `…${account.slice(-4)}` : account;
}

// ---------- тексты ----------

function rub(kop: number): string {
  const celoe = kop % 100 === 0;
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    minimumFractionDigits: celoe ? 0 : 2,
    maximumFractionDigits: celoe ? 0 : 2,
  }).format(kop / 100);
}

export { rub as rubli };

const NAZVANIYA_POLEJ: Record<PoleFormy, string> = {
  recipientType: 'Кто получает',
  bik: 'БИК банка',
  corrAccount: 'Корреспондентский счёт',
  account: 'Расчётный счёт',
  inn: 'ИНН',
  kpp: 'КПП',
  recipientName: 'Получатель',
  amount: 'Сумма',
  consent: 'Согласие',
};

export const TR = {
  // Плашка на входе и регистрации.
  plashka:
    'Вы пришли по приглашению: новым пользователям — +100% к первому пополнению (до 5 000 ₽, от 200 ₽)',

  // Раздел «Партнёрам».
  zagolovok: 'Партнёрам',
  opisanie:
    'Зовите знакомых в AiSearch: им — бонус к первому пополнению, вам — процент с каждого их пополнения.',
  ssylkaZag: 'Ваша ссылка',
  skopirovat: 'Скопировать',
  skopirovano: 'Скопировано',
  ssylkaPodskazka: (dnej: number) =>
    `Отправьте её друзьям, коллегам или подписчикам. Если человек зарегистрируется не сразу, а в течение ${String(dnej)} дней, приглашение всё равно засчитается.`,
  promokod: 'Промокод',
  promokodPodskazka:
    'Если ссылку открыть неудобно, человек может ввести промокод сам — в поле «Промокод» при пополнении баланса. Бонус тот же.',
  staraya: 'Ссылка старого вида',
  starayaPodskazka: 'Она тоже работает — если уже разослана, менять её не нужно.',

  nik: {
    zag: 'Свой ник вместо кода',
    opisanie: (kod: string) =>
      `Имя легче запомнить и продиктовать: aisearch.ru/i/ivan-${PRIMER_OKONCHANIYA} вместо aisearch.ru/i/${kod}. Ник станет и промокодом.`,
    pravilaZag: 'Правила:',
    pravila: [
      `от ${String(NIK_MIN)} до ${String(NIK_MAX)} знаков;`,
      'латинские буквы, цифры и дефис;',
      'хотя бы одна буква;',
      'дефис не в начале, не в конце и не два подряд;',
      'служебные слова и названия наших сервисов заняты;',
      `к нику добавится случайное окончание из 4 знаков, например ivan-${PRIMER_OKONCHANIYA}.`,
    ],
    pole: 'Ник',
    budet: (osnova: string, host: string) =>
      `Ссылка будет такой: ${host}/i/${osnova}-${PRIMER_OKONCHANIYA} — окончание случайное, его выберет сервер.`,
    vybrat: 'Выбрать ник',
    podtverditVopros: (osnova: string) =>
      `Сохранить ник «${osnova}»? К нему добавится случайное окончание, например ${osnova}-${PRIMER_OKONCHANIYA}; сменить потом нельзя.`,
    podtverdit: 'Да, сохранить',
    izmenit: 'Изменить',
    sohranyaem: 'Сохраняем…',
    gotovo: (nik: string, kod: string) =>
      `Готово: ваш ник — ${nik}. Старая ссылка и код ${kod} продолжают работать. Сменить ник больше нельзя.`,
    vybran: (nik: string, kod: string) =>
      `Ваш ник — ${nik}, сменить его нельзя. Старый код ${kod} тоже работает.`,
    prichiny: {
      length: `Ник — от ${String(NIK_MIN)} до ${String(NIK_MAX)} знаков, окончание добавится само.`,
      charset: 'Только латинские буквы, цифры и дефис — без пробелов и кириллицы.',
      hyphen_edge: 'Дефис не может стоять в начале или в конце.',
      double_hyphen: 'Два дефиса подряд нельзя.',
      no_letter: 'Нужна хотя бы одна буква: ник из одних цифр не отличить от старой ссылки.',
      reserved: 'Этот ник зарезервирован. Выберите другой.',
      bad_word: 'В нике есть недопустимое слово.',
    } satisfies Record<PrichinaNika, string>,
    negoden: 'Такой ник не подходит. Проверьте правила выше.',
    zanyat: 'Для этого ника не нашлось свободного окончания. Попробуйте другую основу.',
    uzheVybran: 'Ник уже выбран, сменить его нельзя. Обновите страницу.',
    chasto: 'Слишком много попыток. Попробуйте через час.',
    neVyshlo: 'Не получилось сохранить ник. Попробуйте ещё раз чуть позже.',
  },
  usloviyaZag: 'Как это работает',
  usloviePriglashennomu: (t: UsloviyaReferalki) =>
    `Приглашённому — если он новый пользователь — +${String(t.welcomePercent)}% к первому пополнению, до ${rub(t.welcomeMaxKopecks)} (при пополнении от ${rub(t.welcomeMinTopupKopecks)}).`,
  uslovieVam: (t: UsloviyaReferalki) =>
    `Вам — ${String(t.rewardPercent)}% от каждого пополнения приглашённых, на партнёрский баланс.`,
  uslovieVyvod: (t: UsloviyaReferalki) =>
    `Вывод — от ${rub(t.minPayoutKopecks)} на расчётный счёт ИП, организации или самозанятого.`,

  chislaZag: 'Ваши приглашения',
  prishli: 'Пришли по ссылке',
  oplatili: 'Пополнили баланс',
  summaPopolnenij: 'Сумма их пополнений',
  nachisleno: 'Начислено вам',
  balans: 'Партнёрский баланс',
  dostupno: 'Можно вывести',
  zamorozheno: 'В заявках на вывод',

  vyvodZag: 'Вывести деньги',
  vyvodPoka: (min: number, dostupno: number) =>
    `Вывод откроется, когда на балансе наберётся ${rub(min)}. Сейчас можно вывести ${rub(dostupno)}.`,
  vyvodPodskazka: (min: number, dostupno: number) =>
    `От ${rub(min)} до ${rub(dostupno)}. Сумма откладывается, пока заявка не закрыта.`,
  tipy: { ip: 'ИП', company: 'Организация', self_employed: 'Самозанятый' } as Record<
    string,
    string | undefined
  >,
  polya: NAZVANIYA_POLEJ,
  primerPoluchatelya: {
    ip: 'ИП Иванов Иван Иванович',
    company: 'ООО «Ромашка»',
    self_employed: 'Иванов Иван Иванович',
  } as Record<string, string | undefined>,
  summaRub: 'Сумма, ₽',
  vsyo: 'Всё доступное',
  soglasieDo: 'Согласен на обработку персональных данных для выплаты по ',
  soglasieSsylka: 'политике конфиденциальности',
  otpravit: 'Отправить заявку',
  otpravlyaem: 'Отправляем…',
  podstavleno: 'Реквизиты подставлены из прошлой заявки — проверьте, всё ли актуально.',
  prinyato: 'Заявка отправлена. Мы проверим реквизиты и переведём деньги, статус увидите ниже.',

  zayavkiZag: 'Заявки на вывод',
  zayavokNet: 'Заявок пока нет.',
  statusy: {
    created: 'Принята, проверяем',
    approved: 'Одобрена, готовим перевод',
    paid: 'Выплачена',
    rejected: 'Отклонена',
    error: 'Перевод не прошёл',
  } as Record<string, string | undefined>,
  schet: 'р/с',
  neZagruzilos: 'Не получилось загрузить. Обновите страницу чуть позже.',

  oshibki: {
    recipientType: 'Выберите, кто получает деньги.',
    bik: 'БИК — 9 цифр.',
    corrAccount: 'Корреспондентский счёт — 20 цифр.',
    account: 'Расчётный счёт — 20 цифр.',
    inn10: 'ИНН организации — 10 цифр.',
    inn12: 'ИНН ИП и самозанятого — 12 цифр.',
    kpp: 'КПП — 9 знаков, например 773601001.',
    kppLishnij: 'КПП указывается только у организации.',
    recipientName: 'Напишите получателя полностью, как в банке.',
    summaNeChislo: 'Напишите сумму цифрами, например 10 000.',
    summaMalo: (min: number) => `Вывести можно от ${rub(min)}.`,
    summaMaloObshchee: 'Сумма меньше минимальной для вывода.',
    summaBolshe: (dostupno: number) => `Сейчас можно вывести не больше ${rub(dostupno)}.`,
    nedostatochno: 'На партнёрском балансе меньше, чем вы просите. Обновите страницу.',
    consent: 'Без согласия на обработку данных выплатить не сможем.',
    rekvizityObshchee: 'Банк не примет такие реквизиты. Проверьте их, пожалуйста.',
    kontrolnoe: (pole: PoleFormy) =>
      `${NAZVANIYA_POLEJ[pole]}: номер не сходится — похоже, опечатка в цифрах.`,
    formatPolya: (pole: PoleFormy) => `${NAZVANIYA_POLEJ[pole]}: проверьте, пожалуйста.`,
    chasto: 'Слишком много заявок подряд. Попробуйте через час.',
    sessiya: 'Сеанс истёк. Войдите заново и повторите.',
    nedostupno: 'Партнёрская программа сейчас недоступна.',
    neVyshlo: 'Не получилось отправить заявку. Попробуйте ещё раз чуть позже.',
  },
} as const;
